import React, { useState, useCallback } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useChat } from '../hooks/useChat';
import ChatList from '../components/ChatList';
import ChatWindow from '../components/ChatWindow';
import ContactInfo from '../components/ContactInfo';

const Chat: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    user,
    conversations,
    selectedConversation,
    messages,
    searchResults,
    loadingConversations,
    handleSearch,
    handleSelectUser: selectUser,
    handleSelectConversation: selectConversation,
  } = useChat();

  const [showContactInfo, setShowContactInfo] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');

  const handleSelectUser = useCallback(
    async (...args: Parameters<typeof selectUser>) => {
      await selectUser(...args);
      if (isMobile) setMobileView('chat');
    },
    [selectUser, isMobile]
  );

  const handleSelectConversation = useCallback(
    (...args: Parameters<typeof selectConversation>) => {
      selectConversation(...args);
      if (isMobile) setMobileView('chat');
    },
    [selectConversation, isMobile]
  );

  const handleBack = useCallback(() => {
    setMobileView('list');
    setShowContactInfo(false);
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0a0a14' }}>
      {/* Sidebar */}
      {(!isMobile || mobileView === 'list') && (
        <Box
          sx={{
            width: isMobile ? '100%' : 380,
            minWidth: isMobile ? '100%' : 380,
            borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.04)',
            bgcolor: '#0e0e1a',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
          }}
        >
          <ChatList
            conversations={conversations}
            selectedConversation={selectedConversation}
            searchResults={searchResults}
            onSearch={handleSearch}
            onSelectConversation={handleSelectConversation}
            onSelectUser={handleSelectUser}
            loading={loadingConversations}
          />
        </Box>
      )}

      {/* Chat window */}
      {(!isMobile || mobileView === 'chat') && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
          }}
        >
          <ChatWindow
            conversation={selectedConversation}
            messages={messages}
            currentUserId={user?.uid || ''}
            onBack={isMobile ? handleBack : undefined}
            onShowContactInfo={() => setShowContactInfo(true)}
          />
        </Box>
      )}

      {/* Contact info */}
      {selectedConversation &&
        (isMobile ? (
          <Drawer
            anchor="right"
            open={showContactInfo}
            onClose={() => setShowContactInfo(false)}
            slotProps={{
              paper: {
                sx: { width: '100%', maxWidth: 360, bgcolor: '#0e0e1a' },
              },
            }}
          >
            <ContactInfo
              user={selectedConversation.otherUser}
              onClose={() => setShowContactInfo(false)}
            />
          </Drawer>
        ) : (
          showContactInfo && (
            <Box
              sx={{
                width: 340,
                minWidth: 340,
                borderLeft: '1px solid rgba(255,255,255,0.04)',
                bgcolor: '#0e0e1a',
                height: '100vh',
              }}
            >
              <ContactInfo
                user={selectedConversation.otherUser}
                onClose={() => setShowContactInfo(false)}
              />
            </Box>
          )
        ))}
    </Box>
  );
};

export default Chat;
